<template>
  <UiContainer
    id="header"
    tag="header"
    class="z-20 flex flex-col py-6 md:!relative"
    :class="{ '!px-0 lg:!px-7': isMobile }"
  >
    <div class="flex max-w-full flex-col">
      <div class="flex w-full items-center justify-between" :class="{ 'px-4 lg:px-0': isMobile }">
        <div class="flex w-full items-center justify-between md:w-auto md:pr-5">
          <AppRouterLink
            to="/"
            :tag="externalHomeLink ? 'a' : 'div'"
            :href="externalHomeLink"
            class="flex w-[103px] items-center gap-1"
            :class="city && 'md:mr-5 mr-1'"
          >
            <template v-if="isHomePage">
              <InticketsLogo v-if="!partnerLogoUrl" />
              <img v-else :src="partnerLogoUrl" />
            </template>
            <AfishaLogo v-else />
          </AppRouterLink>
          <div class="relative">
            <UiButton
              v-if="city"
              ref="locationButton"
              severity="quaternary"
              class="flex items-center gap-1 whitespace-nowrap px-1 !text-base"
              :class="{
                '!bg-button-quaternary-hover': showLocationDialog
              }"
              @click.stop="openLocationDialog"
            >
              <UiIcon v-if="isMdAndUp" name="map-pin" class="size-4 min-w-4 text-icon-primary" />
              {{ currentLocation?.displayName }}
              <UiIcon v-if="isMdAndDown" name="chevron-down" class="size-3 min-w-3" />
            </UiButton>
            <ConfirmCity
              v-if="isMdAndUp"
              static
              class="absolute left-0 top-full z-10 mt-3"
              @reject="openLocationDialog({ target: locationButton!.$el } as MouseEvent)"
            />
          </div>

          <UiPopover
            v-if="isMdAndUp"
            ref="popover"
            @show="showLocationDialog = true"
            @close="showLocationDialog = false"
          >
            <ChooseLocation
              class="max-h-[560px] max-w-[400px] rounded-xl"
              @close="closeLocationDialog"
            />
          </UiPopover>

          <UiDialog v-else v-model="showLocationDialog" position="bottom" swipe-close>
            <template #default="{ numberHeight }">
              <ChooseLocation
                :style="{ height: `${numberHeight - 16}px` }"
                class="rounded-t-3xl"
                @close="showLocationDialog = false"
              />
            </template>
          </UiDialog>
        </div>

        <OpenSearchFromHeader v-if="isMdAndUp" />
      </div>
      <nav v-if="isMdAndUp ? true : !hideCategoryOnMobile" class="mt-6">
        <ul
          class="flex gap-7"
          :class="[
            isMobile ? 'scrollbar-hidden overflow-y-scroll' : 'flex-wrap',
            { 'px-4 lg:px-0': isMobile }
          ]"
        >
          <AppRouterLink
            v-for="category in categoriesNav"
            :key="category.id"
            :to="{ name: 'CategoryItemView', params: { id: category.id } }"
            v-slot="{ isActive }"
            tag="li"
            class="w-fit"
          >
            <UiButtonLink size="medium" class="!font-normal">
              <div :class="{ '!text-text-primary': isActive }" class="block rounded-lg">
                {{ category.name }}
              </div>
            </UiButtonLink>
          </AppRouterLink>
        </ul>
      </nav>
    </div>
  </UiContainer>
</template>

<script setup lang="ts">
import { isMobile } from '@basitcodeenv/vue3-device-detect'
import { storeToRefs } from 'pinia'
import { ref, useTemplateRef } from 'vue'
import { ChooseLocation, ConfirmCity } from '@/4_features/Location'
import { OpenSearchFromHeader } from '@/4_features/Search'
import { useConfiguration } from '@/5_entities/Configuration'
import { useLocation } from '@/5_entities/Location'
import { useBreakpoint } from '@/6_shared/lib'
import {
  UiContainer,
  UiButton,
  UiIcon,
  UiPopover,
  UiDialog,
  UiButtonLink,
  InticketsLogo,
  AppRouterLink,
  AfishaLogo
} from '@/6_shared/ui'

type PropType = {
  city?: boolean
  hideCategoryOnMobile?: boolean
  externalHomeLink?: string
  partnerLogoUrl?: string
  isHomePage?: boolean
}
withDefaults(defineProps<PropType>(), {
  city: false,
  hideCategoryOnMobile: false,
  isHomePage: false,
  externalHomeLink: undefined,
  partnerLogoUrl: undefined
})
const {
  md: [isMdAndUp, isMdAndDown]
} = useBreakpoint()
const { categoriesNav } = storeToRefs(useConfiguration())
const { currentLocation } = storeToRefs(useLocation())
const showLocationDialog = ref(false)

const popover = useTemplateRef<typeof UiPopover>('popover')
const locationButton = useTemplateRef<typeof UiButton>('locationButton')

const showPopover = (e: MouseEvent) => {
  popover.value && popover.value.show(e)
  showLocationDialog.value = true
}
const hidePopover = () => {
  popover.value && popover.value.hide()
  showLocationDialog.value = false
}
const openLocationDialog = (e: MouseEvent) => {
  showPopover(e)
}
const closeLocationDialog = () => {
  hidePopover()
}

// const parentHomePage = `${window.parent.location.origin}/`
</script>
